import head from 'lodash/head'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TicketCard } from '@/components/TicketCard'
import { Service, ServiceAvailability } from '@/models/service'
import { serviceQuantitySelector } from '@/store/cartSlice/selectors/serviceQuantitySelector'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import {
  getItemQuantityLabel,
  ItemQuantityBoundary,
} from '@/utils/getItemQuantityLabel'
import {
  getTicketDisplayMaximum,
  getTicketDisplayMinimum,
} from '@/utils/getTicketQuantityDisplayLimits'
import { SINGLE_DAY_PRICE_MODES } from '@/utils/timeSchedules'
import { useAppSelector } from '../../hooks/store'

interface Props {
  availability: ServiceAvailability
  maximumReservationQuantity: number
  maximumSpotQuantity: number
  minimumReservationQuantity: number
  minimumSpotQuantity: number
  ticket: Service
}

export const Ticket = (props: Props) => {
  const { t } = useTranslation()

  const quantity = useAppSelector((state) =>
    serviceQuantitySelector(state, props.ticket)
  )
  const dayDifference = useAppSelector(dayDifferenceSelector)

  const hideTicket = useMemo<boolean>(() => {
    if (props.ticket.timeSchedules.length === 0) {
      return true
    }

    if (
      props.ticket.minimumQuantity > 0 ||
      props.ticket.scheduleType !== 'daily'
    ) {
      return false
    }

    const daysLength = Object.keys(props.ticket.dailyTimeSchedules).length
    if (daysLength === 0) {
      return true
    }

    if (daysLength !== dayDifference) {
      const firstSchedule = head(props.ticket.timeSchedules)
      if (!firstSchedule) {
        // this should never happen
        return true
      }

      return !SINGLE_DAY_PRICE_MODES.includes(firstSchedule.priceMode)
    }

    return false
  }, [props.ticket])

  if (hideTicket) {
    return null
  }

  const global = props.availability?.global ?? Infinity
  const group = props.availability?.group ?? Infinity
  const ticket = props.availability?.ticket ?? Infinity

  const availableQuantity = props.ticket.hasLimit
    ? Math.min(global, group, ticket - quantity)
    : Infinity

  const isAddDisabled = availableQuantity < 1
  const isRemoveDisabled =
    quantity === 0 || quantity <= props.ticket.minimumQuantity

  const groupLimits = {
    maximumReservationQuantity: props.maximumReservationQuantity,
    maximumSpotQuantity: props.maximumSpotQuantity,
    minimumReservationQuantity: props.minimumReservationQuantity,
    minimumSpotQuantity: props.minimumSpotQuantity,
  }

  const displayMinimum = getTicketDisplayMinimum(
    props.ticket,
    groupLimits,
    'reservation'
  )
  const displayMaximum = getTicketDisplayMaximum(
    props.ticket,
    groupLimits,
    'reservation'
  )

  const minLabel =
    displayMinimum !== null
      ? getItemQuantityLabel({
          boundary: ItemQuantityBoundary.MIN,
          flatLimitAmount: displayMinimum,
          limitType: props.ticket.minimumLimitType,
        }) ?? t('listItem.min', { value: displayMinimum })
      : ''

  const maxLabel =
    props.ticket.scheduleType === 'daily' && displayMaximum !== null
      ? getItemQuantityLabel({
          boundary: ItemQuantityBoundary.MAX,
          flatLimitAmount: displayMaximum,
          limitType: props.ticket.maximumLimitType,
        }) ?? t('listItem.max', { value: displayMaximum })
      : ''

  return (
    <TicketCard
      isAddDisable={isAddDisabled}
      isRemoveDisabled={isRemoveDisabled}
      maxLabel={maxLabel}
      minLabel={minLabel}
      quantity={quantity}
      ticket={props.ticket}
    />
  )
}
